import React, { PropsWithChildren, ReactNode, useContext, useMemo, useRef, useState } from 'react'

import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useFloating,
  useId,
  useInteractions,
  useRole,
} from '@floating-ui/react-dom-interactions'
import { FullScreenOverlay } from './FullScreenOverlay'

export type CallbackFn = (close: () => void) => void

type OverlayContext = {
  open: boolean
  content: ReactNode
  windowContentRef: React.RefObject<HTMLDivElement>
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  setContent: React.Dispatch<React.SetStateAction<ReactNode>>
  openDialog: boolean
  dialogContent: ReactNode
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>
  setDialogContent: React.Dispatch<React.SetStateAction<ReactNode>>
}

const OverlayContext = React.createContext<OverlayContext>({} as OverlayContext)

export const OverlayProvider = (props: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false)
  const windowContentRef = useRef<HTMLDivElement>(null)
  const [content, setContent] = useState<ReactNode>(() => null)
  const [openDialog, setOpenDialog] = useState(false)
  const [dialogContent, setDialogContent] = useState<ReactNode>(() => null)

  return (
    <OverlayContext.Provider
      value={{
        open,
        content,
        setContent,
        setOpen,
        openDialog,
        setOpenDialog,
        dialogContent,
        setDialogContent,
        windowContentRef,
      }}
    >
      {props.children}
    </OverlayContext.Provider>
  )
}

export function DialogOverlay({ children }: PropsWithChildren) {
  return (
    <div
      className="w-screen min-h-screen"
      style={{
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(25, 25, 25, 0.8)',
      }}
    >
      <div className="bg-primaryBgColor text-primaryFontColor rounded-md min-w-[400px]">
        {children}
      </div>
    </div>
  )
}

export const useDialog = () => {
  const dialog = useContext(OverlayContext)
  const { setOpenDialog, setDialogContent } = useMemo(() => dialog, [])
  return (content: ReactNode) => {
    setDialogContent(() => <DialogOverlay>{content}</DialogOverlay>)
    setOpenDialog(true)
  }
}
export const useOverlay = (dialog?: boolean) => {
  const overlayContext = useContext(OverlayContext)
  const { setOpen, setContent } = useMemo(() => overlayContext, [])

  return (content: ReactNode, onClose?: CallbackFn) => {
    setContent(() => {
      return dialog ? (
        <DialogOverlay>{content}</DialogOverlay>
      ) : (
        <FullScreenOverlay onClose={onClose}>{content}</FullScreenOverlay>
      )
    })
    setOpen(true)
  }
}
export const useFullScreenOverlay = () => {
  const overlayContext = useContext(OverlayContext)
  const { setOpen, setContent } = useMemo(() => overlayContext, [])

  return (content: ReactNode, onClose?: CallbackFn) => {
    setContent(() => <FullScreenOverlay onClose={onClose}>{content}</FullScreenOverlay>)
    setOpen(true)
  }
}
export const useOverlayVisibility = () => {
  const dialog = useContext(OverlayContext)
  const { setOpen, open, windowContentRef } = useMemo(() => dialog, [])
  return { open, setOpen, windowContentRef }
}

export const useDialogVisibility = () => {
  const dialog = useContext(OverlayContext)
  const { openDialog, setOpenDialog, windowContentRef } = useMemo(() => dialog, [])
  return { openDialog, setOpenDialog, windowContentRef }
}

/**Overlay component content can be set with useDialog and useFullScreenOverlay hooks and visibility controlled with useOverlayVisibility hook*/
export const Overlay = () => {
  const { open, setOpen, content } = useContext(OverlayContext)

  return (
    <BaseOverlay open={open} setOpen={setOpen}>
      {content}
    </BaseOverlay>
  )
}

/**Overlay component content can be set with useDialog and useFullScreenOverlay hooks and visibility controlled with useOverlayVisibility hook*/
export const OverlayDialog = () => {
  const { openDialog, setOpenDialog, dialogContent } = useContext(OverlayContext)

  return (
    <BaseOverlay open={openDialog} setOpen={setOpenDialog} zIndex={1401}>
      {dialogContent}
    </BaseOverlay>
  )
}

export const BaseOverlay = (props: {
  open: boolean
  setOpen?: (open: boolean) => void
  children?: ReactNode
  zIndex?: number
}) => {
  const { floating, context } = useFloating({
    open: props.open,
    onOpenChange: props.setOpen,
  })

  const id = useId()
  const labelId = `${id}-label`
  const descriptionId = `${id}-description`

  const interactions = useInteractions([useClick(context), useRole(context)])

  return (
    <FloatingPortal>
      {props.open && (
        <FloatingOverlay
          lockScroll
          style={{
            display: 'grid',
            placeItems: 'center',
            zIndex: props.zIndex ?? 1400,
          }}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        >
          <FloatingFocusManager context={context}>
            <div
              ref={floating}
              aria-labelledby={labelId}
              aria-describedby={descriptionId}
              {...interactions.getFloatingProps()}
            >
              {props.children}
            </div>
          </FloatingFocusManager>
        </FloatingOverlay>
      )}
    </FloatingPortal>
  )
}
